/**
 * PersistentChatwoot - Keeps Chatwoot iframe mounted across navigation
 *
 * URL sync: Chatwoot posts CHATWOOT_ROUTE_CHANGE via afterEach Vue Router hook.
 * Parent updates browser URL to /inbox{path} so users can share deep links.
 *
 * The iframe is:
 * - Mounted when user first visits /inbox (lazy initialization)
 * - Kept mounted when navigating away (hidden with CSS)
 * - Shown instantly when returning to /inbox
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { ChatwootEmbed } from "@/components/ChatwootEmbed";
import { ChatOrderSheet, type CheckoutLinkPayload } from "@/features/inbox/ChatOrderSheet";
import { ChannelGuideOverlay } from "@/features/inbox/ChannelGuideOverlay";
import { useAuthStore } from "@/lib/store/authStore";
import { isSignificantNavigation } from "./urlSyncUtils";

export function PersistentChatwoot() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnInboxPage = location.pathname.startsWith("/inbox");

  // Chat Order Sheet state
  const [orderSheetOpen, setOrderSheetOpen] = useState(false);
  const [orderConversationId, setOrderConversationId] = useState<string>();
  // Track if we've ever visited /inbox (lazy mount)
  const [hasVisitedInbox, setHasVisitedInbox] = useState(false);

  // Track if iframe is ready (for showing content vs loading state)
  const [_isReady, setIsReady] = useState(false);

  // Remount key — incremented on logout to force iframe destruction + fresh SSO
  const [mountKey, setMountKey] = useState(0);

  // URL sync refs
  const lastSyncedPath = useRef('');
  const initialPathRef = useRef<string | undefined>(undefined);
  const isOnInboxPageRef = useRef(isOnInboxPage);
  isOnInboxPageRef.current = isOnInboxPage;

  // Check if Chatwoot is provisioned
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  // When user logs out, reset state so iframe is destroyed.
  // On re-login + next /inbox visit, a fresh SSO flow starts. (OPS-1489)
  const wasAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      // User logged out — destroy iframe state
      setHasVisitedInbox(false);
      setIsReady(false);
      setMountKey((prev) => prev + 1);
      lastSyncedPath.current = '';
      initialPathRef.current = undefined;
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // First mount: capture deep link path from URL
  useEffect(() => {
    if (isOnInboxPage && !hasVisitedInbox) {
      const subPath = location.pathname.slice(6); // Remove "/inbox"
      // Strip /accounts/{id} prefix from deep link paths — buildDashboardUrl
      // in ChatwootEmbed adds it back, so passing it here would double it.
      // e.g. "/accounts/17/conversations/123" → "/conversations/123"
      const strippedPath = subPath.replace(/^\/accounts\/\d+/, '') || undefined;
      initialPathRef.current = strippedPath;
      lastSyncedPath.current = subPath;
      setHasVisitedInbox(true);
    }
  }, [isOnInboxPage, hasVisitedInbox, location.pathname]);

  // Handle returning to /inbox — either restore last path or navigate iframe to new deep link
  const suppressRouteSyncUntil = useRef(0);

  useEffect(() => {
    if (!isOnInboxPage || !hasVisitedInbox) return;

    const incomingSubPath = location.pathname.slice(6); // Remove "/inbox"

    // User arrived with a NEW deep link (e.g., from dashboard conversation click)
    // → change iframe src directly to navigate Chatwoot to that conversation
    if (incomingSubPath && incomingSubPath !== lastSyncedPath.current) {
      lastSyncedPath.current = incomingSubPath;

      // Suppress route sync from iframe for 3s so Chatwoot's initial
      // ROUTE_CHANGE (to /dashboard) doesn't overwrite our deep link URL
      suppressRouteSyncUntil.current = Date.now() + 6000;

      const iframe = document.querySelector<HTMLIFrameElement>(
        '.chatwoot-embed-wrapper iframe'
      );
      if (iframe) {
        const base = iframe.src.split('/app/')[0] || 'https://inbox.zaviago.com';
        // Preserve /accounts/{id} prefix so Chatwoot Vue Router matches the route
        const accountMatch = iframe.src.match(/\/app(\/accounts\/\d+)/);
        const accountPrefix = accountMatch?.[1] ?? '';
        iframe.src = `${base}/app${accountPrefix}${incomingSubPath}`;
      }
      return;
    }

    // No deep link — restore the last synced path in the URL bar
    if (lastSyncedPath.current) {
      const expectedPath = `/inbox${lastSyncedPath.current}`;
      if (location.pathname !== expectedPath) {
        navigate(expectedPath, { replace: true });
      }
    }
  }, [isOnInboxPage, hasVisitedInbox, navigate, location.pathname]);

  // Track the Chatwoot iframe origin for secure postMessage sends
  const chatwootOriginRef = useRef<string | null>(null);

  // Listen for postMessages from Chatwoot iframe
  useEffect(() => {
    const ALLOWED_ORIGINS = ['https://inbox.zaviago.com'];
    if (import.meta.env.DEV) {
      ALLOWED_ORIGINS.push('http://localhost:3000');
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.data?.type) return;

      // Validate origin for Chatwoot messages
      const isChatwootMsg = typeof event.data.type === 'string' && event.data.type.startsWith('CHATWOOT_');
      if (isChatwootMsg) {
        const originAllowed = ALLOWED_ORIGINS.some((o) => event.origin.startsWith(o));
        if (!originAllowed) return;
        chatwootOriginRef.current = event.origin;
      }

      // Route change sync
      if (event.data.type === 'CHATWOOT_ROUTE_CHANGE') {
        if (!isOnInboxPageRef.current) return;
        // Suppress route sync briefly after deep link navigation to prevent
        // Chatwoot's initial /dashboard route from overwriting our target URL
        if (Date.now() < suppressRouteSyncUntil.current) return;

        const { path } = event.data.payload || {};
        if (!path || typeof path !== 'string') return;

        const cleanPath = path.startsWith('/app') ? path.slice(4) : path;
        if (lastSyncedPath.current === cleanPath) return;
        const prevPath = lastSyncedPath.current;
        lastSyncedPath.current = cleanPath;

        const shouldPush = isSignificantNavigation(prevPath, cleanPath);
        navigate(`/inbox${cleanPath}`, { replace: !shouldPush });
        return;
      }

      // Open order builder sheet
      if (event.data.type === 'CHATWOOT_OPEN_ORDER_BUILDER') {
        const { conversation_id } = event.data.payload || {};
        setOrderConversationId(conversation_id);
        setOrderSheetOpen(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate]);

  // Send checkout link back to Chatwoot iframe via PostMessage
  const handleCheckoutLinkReady = useCallback((payload: CheckoutLinkPayload) => {
    const iframe = document.querySelector<HTMLIFrameElement>(
      '.chatwoot-embed-wrapper iframe'
    );
    const targetOrigin = chatwootOriginRef.current;
    if (iframe?.contentWindow && targetOrigin) {
      // grand_total from ERPNext already includes items + personalization + shipping
      // (shipping is in Sales Taxes and Charges table). Do NOT add shipping on top.
      const total = Number(payload.grand_total) || 0;
      const currencySymbol = payload.currency === 'THB' ? '฿' : payload.currency;
      iframe.contentWindow.postMessage(
        {
          type: 'CHATWOOT_INSERT_CHECKOUT_LINK',
          payload: {
            checkout_url: payload.url,
            grand_total: total,
            subtotal: payload.subtotal,
            currency: payload.currency,
            shipping: payload.shipping ?? 0,
            products: payload.products,
            customer: payload.customer,
            message: `Order ${currencySymbol}${total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
          },
        },
        targetOrigin
      );
    }
  }, []);

  const handleReady = useCallback(() => {
    console.log("[PersistentChatwoot] Ready");
    setIsReady(true);
  }, []);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentChatwoot] Error:", message);
  }, []);

  // Render ChatOrderSheet always (it's a portal/sheet, independent of iframe)
  // Only render the Chatwoot iframe when user has visited /inbox and is provisioned
  const showIframe = hasVisitedInbox && isProvisioned;

  return (
    <>
      {showIframe && (
        <div
          className="fixed inset-0 top-0 z-10 pb-14 md:pb-0 md:left-[72px] md:right-[48px]"
          style={{
            visibility: isOnInboxPage ? "visible" : "hidden",
            pointerEvents: isOnInboxPage ? "auto" : "none",
          }}
        >
          <ChatwootEmbed
            key={mountKey}
            initialPath={initialPathRef.current}
            className="h-full w-full"
            onReady={handleReady}
            onError={handleError}
          />
        </div>
      )}

      {/* Floating channel guide — for chat-intent users who just completed onboarding */}
      {isOnInboxPage && <ChannelGuideOverlay />}

      {/* Chat Order Sheet — always renderable, triggered by Chatwoot PostMessage */}
      <ChatOrderSheet
        open={orderSheetOpen}
        onOpenChange={setOrderSheetOpen}
        conversationId={orderConversationId}
        onCheckoutLinkReady={handleCheckoutLinkReady}
      />
    </>
  );
}
